import React, { useState, useEffect } from 'react';
import { useNotifications } from '../hooks/useNotifications';

// notification system component
function NotificationSystem() {
  const {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    markAllAsRead,
    clearAll
  } = useNotifications(true);

  const [showPanel, setShowPanel] = useState(false);
  const [toasts, setToasts] = useState([]);

  // show toast when new notification arrives
  useEffect(() => {
    if (notifications.length > 0 && !notifications[0].read) {
      const newNotif = notifications[0];
      setToasts(prev => [newNotif, ...prev].slice(0, 3)); // max 3 toasts

      // auto dismiss after 5 seconds
      const timer = setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== newNotif.id));
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [notifications]);

  // dismiss toast manually
  const dismissToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    markAsRead(id);
  };

  // format time for display
  const formatTime = (timestamp) => {
    const diff = Math.floor((Date.now() - new Date(timestamp).getTime()) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // get color classes based on notification type
  const getTypeColor = (type) => {
    const colors = {
      success: 'text-green-500',
      warning: 'text-yellow-500',
      error: 'text-red-500',
      info: 'text-blue-500'
    };
    return colors[type] || colors.info;
  };

  const getToastColor = (type) => {
    const colors = {
      success: 'bg-green-50 border-green-200 text-green-800',
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      error: 'bg-red-50 border-red-200 text-red-800',
      info: 'bg-blue-50 border-blue-200 text-blue-800'
    };
    return colors[type] || colors.info;
  };

  return (
    <>
      {/* connection status indicator */}
      <div className={`fixed bottom-4 left-4 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium shadow-lg
        ${isConnected ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}
      >
        <span className={`w-2 h-2 rounded-full animate-pulse ${isConnected ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
        {isConnected ? 'Connected' : 'Connecting...'}
      </div>

      {/* notification bell button */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="fixed top-4 right-4 z-50 p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow"
      >
        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* notification panel */}
      {showPanel && (
        <div className="fixed top-14 right-4 z-50 w-80 bg-white rounded-xl shadow-2xl border overflow-hidden">
          <div className="flex justify-between items-center p-3 bg-gray-50 border-b">
            <h3 className="font-semibold text-gray-800">Notifications</h3>
            <div className="flex gap-3 text-xs">
              {unreadCount > 0 && (
                <button onClick={markAllAsRead} className="text-blue-600 hover:text-blue-700">
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button onClick={clearAll} className="text-red-500 hover:text-red-600">
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => markAsRead(notif.id)}
                  className={`p-3 border-b cursor-pointer hover:bg-gray-50 flex gap-3
                    ${!notif.read ? 'bg-blue-50' : ''}`}
                >
                  <span className={`mt-0.5 ${getTypeColor(notif.type)}`}>●</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-700">{notif.message}</p>
                    <p className="text-xs text-gray-400 mt-1">{formatTime(notif.timestamp)}</p>
                  </div>
                  {!notif.read && (
                    <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* toast notifications */}
      <div className="fixed top-14 right-14 z-40 flex flex-col gap-3 max-w-xs">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`p-4 rounded-lg shadow-lg border flex gap-3 items-start animate-slideIn ${getToastColor(toast.type)}`}
          >
            <p className="flex-1 text-sm">{toast.message}</p>
            <button
              onClick={() => dismissToast(toast.id)}
              className="text-gray-400 hover:text-gray-600 text-lg leading-none"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </>
  );
}

export default NotificationSystem;
